
import React, { useState, useRef, useEffect } from 'react';
import { getFashionAdvice, generateFashionImage, summarizePreferences } from '../services/geminiService';
import type { ChatMessage } from '../types';
import { Trash2Icon, XIcon, ThumbsUpIcon, ThumbsDownIcon, SendIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { getSystemHelpContext } from '../data/helpData';
import { useAppContext } from '../AppContext';

interface StyleBotProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    activeAgentId?: string;
}

const StyleBot: React.FC<StyleBotProps> = ({ isOpen, setIsOpen, activeAgentId = 'fashionExpert' }) => {
    const { t, lang } = useTranslation();
    const { userProfile, wardrobe, savedOutfits, lookbooks } = useAppContext();

    // Initialize messages.
    const [messages, setMessages] = useState<ChatMessage[]>([]);

    const [userPreferences, setUserPreferences] = useState<string>(() => localStorage.getItem('styleBotPreferences') || '');
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeFeedback, setActiveFeedback] = useState<string | null>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);

    // Load initial history
    useEffect(() => {
        try {
            const savedMessages = localStorage.getItem('styleBotHistory');
            if (savedMessages) {
                setMessages(JSON.parse(savedMessages));
            } else {
                setMessages([{ id: `model-init`, role: 'model', text: t('stylingAdvisorPage_greeting') }]);
            }
        } catch (error) {
            setMessages([{ id: `model-init`, role: 'model', text: t('stylingAdvisorPage_greeting') }]);
        }
    }, [t]);

    // Handle agent switch
    useEffect(() => {
        if (!isOpen) return;

        // If switching to questionnaire, provide a specific helpful prompt if history is empty or last msg wasn't from it
        if (activeAgentId === 'styleQuestionnaire') {
            // Only add if it's not already the last message to avoid dupes
            const lastMsg = messages[messages.length - 1];
            const introText = t('styleBot_questionnaire_intro');
            if (!lastMsg || (lastMsg.role !== 'model' || lastMsg.text !== introText)) {
                // Optionally clear history for a fresh questionnaire start or append
                // Here we append a new "start" message
                setMessages(prev => [...prev, { id: `model-q-${Date.now()}`, role: 'model', text: introText }]);
            }
        }
    }, [activeAgentId, isOpen, t]);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    useEffect(() => {
        localStorage.setItem('styleBotHistory', JSON.stringify(messages));

        const userMessages = messages.filter(m => m.role === 'user');
        if (userMessages.length > 0 && userMessages.length % 3 === 0) {
            summarizePreferences(messages).then(summary => {
                if (summary) {
                    setUserPreferences(summary);
                    localStorage.setItem('styleBotPreferences', summary);
                }
            });
        }
    }, [messages]);

    const handleFeedback = (messageId: string, rating: 'up' | 'down', tag?: string) => {
        setMessages(prevMessages =>
            prevMessages.map(msg =>
                msg.id === messageId ? { ...msg, feedback: { rating, tag: tag || (typeof msg.feedback === 'object' ? msg.feedback?.tag : undefined) } } : msg
            )
        );
        setActiveFeedback(null);
    };

    const handleClearHistory = () => {
        const initialText = activeAgentId === 'styleQuestionnaire' ? t('styleBot_questionnaire_intro') : 'History cleared. How can I help you today?';
        setMessages([{ id: `model-${Date.now()}`, role: 'model', text: initialText }]);
        setUserPreferences('');
        localStorage.removeItem('styleBotPreferences');
    };

    const handleSendMessage = async (e?: React.FormEvent, customText?: string) => {
        if (e) e.preventDefault();
        const textToSend = customText || input;

        if (!textToSend.trim() || isLoading) return;

        const userMessage: ChatMessage = { id: `user-${Date.now()}`, role: 'user', text: textToSend };
        setMessages(prev => [...prev, userMessage]);
        if (!customText) setInput('');

        setIsLoading(true);
        setError(null);

        const currentInput = textToSend;
        const imageKeywords = ['show me', 'visualize', 'generate', 'picture of', 'image of', 'draw'];
        const isImageRequest = imageKeywords.some(keyword => currentInput.toLowerCase().includes(keyword));

        if (isImageRequest) {
            const imageData = await generateFashionImage(currentInput);
            if (imageData.startsWith('API_KEY_ERROR') || imageData.startsWith('IMAGE_GENERATION_ERROR')) {
                const errorMessage = imageData.startsWith('API_KEY_ERROR')
                    ? 'Could not find API Key. Please ensure it is configured correctly.'
                    : 'Sorry, I had trouble creating that image.';
                const modelMessage: ChatMessage = { id: `model-error-${Date.now()}`, role: 'model', text: errorMessage, isError: true };
                setMessages(prev => [...prev, modelMessage]);
            } else if (imageData.includes("couldn't create an image")) {
                const modelMessage: ChatMessage = { id: `model-${Date.now()}`, role: 'model', text: imageData };
                setMessages(prev => [...prev, modelMessage]);
            } else {
                const modelMessage: ChatMessage = {
                    id: `model-${Date.now()}`,
                    role: 'model',
                    text: `Here's some visual inspiration for "${currentInput}":`,
                    imageUrl: `data:image/png;base64,${imageData}`
                };
                setMessages(prev => [...prev, modelMessage]);
            }
        } else {
            // Build Context
            let promptContext = getSystemHelpContext(t); // Inject App Knowledge Base
            promptContext += `\n\nUser Preferences: ${userPreferences}`;

            // Add rich context from AppContext
            const wardrobeSummary = wardrobe.length > 0
                ? `User's Wardrobe (${wardrobe.length} items): ${wardrobe.slice(0, 10).map(i => i.nameKey).join(', ')}${wardrobe.length > 10 ? '...' : ''}`
                : 'User has no items in wardrobe.';

            const outfitsSummary = savedOutfits.length > 0
                ? `Saved Outfits: ${savedOutfits.slice(0, 5).map(o => o.name).join(', ')}`
                : '';

            const lookbooksSummary = lookbooks.length > 0
                ? `Lookbooks: ${lookbooks.slice(0, 3).map(l => l.name).join(', ')}`
                : '';

            promptContext += `\n\nContextual Data:\n${wardrobeSummary}\n${outfitsSummary}\n${lookbooksSummary}`;

            if (activeAgentId === 'minimalistStylist') promptContext += " Act as a minimalist stylist.";

            // Specialized Prompt for Questionnaire
            if (activeAgentId === 'styleQuestionnaire') {
                promptContext += " Act as a structured style interviewer building a user profile. You MUST prioritize asking about: 1. Gender Identity, 2. Body Shape, 3. Face Shape, 4. Color Season, 5. Personal Style. If the user provides this info, acknowledge it and move to the next missing attribute. Keep it conversational.";
            }

            if (activeAgentId === 'vogueEditor') promptContext += " Act as a high-fashion Vogue editor.";
            if (activeAgentId === 'business') promptContext += " Focus on professional business attire advice.";

            const responseText = await getFashionAdvice(currentInput, promptContext, lang, userProfile);
            if (responseText.startsWith("Could not find API Key") || responseText.startsWith("An error occurred")) {
                const modelMessage: ChatMessage = { id: `model-error-${Date.now()}`, role: 'model', text: responseText, isError: true };
                setMessages(prev => [...prev, modelMessage]);
            } else {
                const modelMessage: ChatMessage = { id: `model-${Date.now()}`, role: 'model', text: responseText };
                setMessages(prev => [...prev, modelMessage]);
            }
        }

        setIsLoading(false);
    };

    // Quick Replies for Questionnaire
    const QUESTIONNAIRE_CHIPS = [
        t('styleBot_chip_female'), t('styleBot_chip_male'), t('styleBot_chip_non_binary'),
        t('styleBot_chip_hourglass'), t('styleBot_chip_pear'), t('styleBot_chip_rectangle'), t('styleBot_chip_athletic'),
        t('styleBot_chip_oval_face'), t('styleBot_chip_square_face'), t('styleBot_chip_round_face'), t('styleBot_chip_heart_face'),
        t('styleBot_chip_winter'), t('styleBot_chip_summer'), t('styleBot_chip_autumn'), t('styleBot_chip_spring'),
        t('styleBot_chip_minimalist'), t('styleBot_chip_boho'), t('styleBot_chip_classic'), t('styleBot_chip_streetwear')
    ];

    if (!isOpen) return null;

    return (
        <div className="absolute inset-0 flex flex-col bg-white dark:bg-gray-800 z-10">
            <header className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
                <h3 className="font-serif-display text-lg text-gray-900 dark:text-white">
                    {activeAgentId !== 'fashionExpert'
                        ? t(`stylingAdvisorPage_agent_${activeAgentId}_name` as any)
                        : 'Elysian Style Bot'}
                </h3>
                <div className="flex items-center space-x-2">
                    <button onClick={handleClearHistory} title="Clear Chat History" className="text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors">
                        <Trash2Icon className="w-5 h-5" />
                    </button>
                    <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-800 dark:hover:text-white" aria-label={t('common_close') || "Close"}>
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>
            </header>
            <main className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 custom-scrollbar">
                <div className="space-y-4">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-fade-in-up`}>
                            <div className={`max-w-[85%] rounded-lg ${msg.role === 'user'
                                ? 'bg-indigo-600 text-white'
                                : msg.isError
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                    : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600'
                                }`}>
                                {msg.imageUrl && (
                                    <img src={msg.imageUrl} alt="Generated fashion inspiration" className="rounded-t-lg" />
                                )}
                                <p className="text-sm whitespace-pre-wrap p-3">{msg.text}</p>
                            </div>
                            {msg.role === 'model' && !(msg.text?.includes("API Key")) && !msg.isError && (
                                <div className="mt-1 flex items-center space-x-2 relative">
                                    <button
                                        onClick={() => handleFeedback(msg.id!, 'up')}
                                        disabled={!!msg.feedback}
                                        className={`p-1 rounded-full transition-colors ${(typeof msg.feedback === 'object' && msg.feedback?.rating === 'up') ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50'}`}
                                        aria-label="Like response"
                                    >
                                        <ThumbsUpIcon className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => handleFeedback(msg.id!, 'down')}
                                        disabled={!!msg.feedback}
                                        className={`p-1 rounded-full transition-colors ${(typeof msg.feedback === 'object' && msg.feedback?.rating === 'down') ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50'}`}
                                        aria-label="Dislike response"
                                    >
                                        <ThumbsDownIcon className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start animate-fade-in-up">
                            <div className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-3 rounded-lg flex items-center space-x-2 border border-gray-200 dark:border-gray-600">
                                <span className="text-sm">{t('stylingAdvisorPage_thinking')}</span>
                                <div className="w-1.5 h-1.5 bg-indigo-600 dark:bg-indigo-400 rounded-full animate-pulse"></div>
                                <div className="w-1.5 h-1.5 bg-indigo-600 dark:bg-indigo-400 rounded-full animate-pulse delay-200"></div>
                                <div className="w-1.5 h-1.5 bg-indigo-600 dark:bg-indigo-400 rounded-full animate-pulse delay-400"></div>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
            </main>

            {/* Quick Replies for Questionnaire */}
            {activeAgentId === 'styleQuestionnaire' && !isLoading && (
                <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-2">
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1 px-1">
                        {QUESTIONNAIRE_CHIPS.map(chip => (
                            <button
                                key={chip}
                                onClick={() => handleSendMessage(undefined, chip)}
                                className="whitespace-nowrap px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900/50 transition-colors"
                            >
                                {chip}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <footer className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t('stylingAdvisorPage_placeholder')}
                        className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 rounded-full py-2 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition text-sm"
                        disabled={isLoading}
                    />
                    <button type="submit" className="p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition" disabled={isLoading || !input.trim()} aria-label="Send message">
                        <SendIcon className="w-5 h-5" />
                    </button>
                </form>
            </footer>
        </div>
    );
};

export default StyleBot;
